/**
 * Forms API Service
 * 테넌트별 폼 시스템과 관련된 API 호출을 담당합니다.
 */

// API 기본 URL 설정
const API_BASE_URL = '/api';

/**
 * 테넌트별 사용 가능한 폼 목록을 조회합니다.
 * @param {string} tenantId - 테넌트 ID (예: 'evcheck')
 * @returns {Promise<Object>} 폼 목록 및 메타데이터
 */
export const getForms = async (tenantId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/forms/?tenant_id=${tenantId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // JWT 쿠키 포함
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching forms:', error);
    throw error;
  }
};

/**
 * 폼 데이터를 제출합니다.
 * @param {string} formId - 폼 ID
 * @param {string} mode - 'insert' 또는 'bulk_insert'
 * @param {Object|Array} input - 제출할 데이터 (단일 객체 또는 배열)
 * @returns {Promise<Object>} 제출 결과
 */
export const submitForm = async (formId, mode, input) => {
  try {
    const response = await fetch(`${API_BASE_URL}/form_submit/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // JWT 쿠키 포함
      body: JSON.stringify({
        form_id: formId,
        mode: mode,
        input: input
      })
    });

    // 응답이 실패한 경우 상세 에러 정보 추출
    if (!response.ok) {
      let errorInfo = {
        status: response.status,
        statusText: response.statusText,
        message: `HTTP error! status: ${response.status}`
      };

      try {
        // 백엔드에서 JSON 에러 응답을 보냈는지 확인
        const errorData = await response.json();
        if (errorData) {
          errorInfo = { ...errorInfo, ...errorData };
          // 사용자 친화적인 에러 메시지 생성
          errorInfo.userMessage = errorData.error_message || errorData.error || errorInfo.message;
        }
      } catch (jsonError) {
        // JSON 파싱 실패시 기본 메시지 사용
        console.warn('Failed to parse error response as JSON:', jsonError);
      }

      const error = new Error(errorInfo.userMessage || errorInfo.message);
      error.details = errorInfo;
      throw error;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error submitting form:', error);
    throw error;
  }
};

/**
 * 폼 데이터를 검색합니다.
 * @param {string} formId - 폼 ID
 * @param {Object} searchInput - 검색 조건
 * @returns {Promise<Object>} 검색 결과
 */
export const searchFormRecords = async (formId, searchInput) => {
  try {
    // searchInput이 null, undefined이거나 빈 값이면 빈 객체로 처리
    const finalInput = searchInput || {};

    const response = await fetch(`${API_BASE_URL}/form_record_search/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // JWT 쿠키 포함
      body: JSON.stringify({
        form_id: formId,
        mode: 'search',
        input: finalInput
      })
    });

    // 응답이 실패한 경우 상세 에러 정보 추출
    if (!response.ok) {
      let errorInfo = {
        status: response.status,
        statusText: response.statusText,
        message: `HTTP error! status: ${response.status}`
      };

      try {
        // 백엔드에서 JSON 에러 응답을 보냈는지 확인
        const errorData = await response.json();
        if (errorData) {
          errorInfo = { ...errorInfo, ...errorData };
          // 사용자 친화적인 에러 메시지 생성
          errorInfo.userMessage = errorData.error_message || errorData.error || errorInfo.message;
        }
      } catch (jsonError) {
        // JSON 파싱 실패시 기본 메시지 사용
        console.warn('Failed to parse error response as JSON:', jsonError);
      }

      const error = new Error(errorInfo.userMessage || errorInfo.message);
      error.response = { data: errorInfo }; // FormRenderer에서 접근할 수 있도록
      throw error;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error searching form records:', error);
    throw error;
  }
};

/**
 * 폼 데이터를 삭제합니다.
 * @param {string} formId - 폼 ID
 * @param {Object} deleteInput - 삭제할 데이터 (보통 user_id나 primary key)
 * @returns {Promise<Object>} 삭제 결과
 */
export const deleteFormRecord = async (formId, deleteInput) => {
  try {
    const response = await fetch(`${API_BASE_URL}/form_delete/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // JWT 쿠키 포함
      body: JSON.stringify({
        form_id: formId,
        input: deleteInput
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error deleting form record:', error);
    throw error;
  }
};

/**
 * 삭제 전 FK 참조 데이터를 확인합니다.
 * @param {string} formId - 폼 ID
 * @param {Object} recordData - 삭제할 레코드 데이터
 * @returns {Promise<Object>} 참조 데이터 정보
 */
export const checkFormReferences = async (formId, recordData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/form_reference_check/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // JWT 쿠키 포함
      body: JSON.stringify({
        form_id: formId,
        input: recordData
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error checking form references:', error);
    throw error;
  }
};

/**
 * 범용 Lookup 검색을 수행합니다.
 * @param {Object} lookupParams - Lookup 검색 파라미터
 * @param {string} lookupParams.source_form_id - 검색할 소스 폼 ID
 * @param {string} lookupParams.search_query - 검색어
 * @param {Array} lookupParams.search_fields - 검색 대상 필드들
 * @param {number} lookupParams.limit - 결과 개수 제한 (기본값: 20)
 * @returns {Promise<Object>} 검색된 옵션 목록
 */
export const lookupSearch = async (lookupParams) => {
  try {
    const {
      source_form_id,
      search_query = '',
      search_fields = [],
      limit = 20
    } = lookupParams;

    const response = await fetch(`${API_BASE_URL}/form_lookup/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // JWT 쿠키 포함
      body: JSON.stringify({
        source_form_id,
        search_query,
        search_fields,
        limit
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error performing lookup search:', error);
    throw error;
  }
};

/**
 * LLM을 통한 자연어 폼 해석 (향후 구현 예정)
 * @param {string} naturalLanguageInput - 사용자의 자연어 입력
 * @param {string} tenantId - 현재 테넌트 ID
 * @returns {Promise<Object>} 해석된 폼 정보 {form_id, mode, input}
 */
export const interpretNaturalLanguageInput = async (naturalLanguageInput, tenantId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/llm_form_interpret/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // JWT 쿠키 포함
      body: JSON.stringify({
        input: naturalLanguageInput,
        tenant_id: tenantId
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error interpreting natural language input:', error);
    throw error;
  }
};

/**
 * 현재 테넌트 정보를 쿠키에서 추출합니다.
 * @returns {string|null} 현재 테넌트 ID
 */
export const getCurrentTenant = () => {
  try {
    // 쿠키에서 테넌트 정보 추출 (CookieService와 유사한 패턴)
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'current_tenant') {
        return decodeURIComponent(value);
      }
    }
    return null;
  } catch (error) {
    console.error('Error getting current tenant:', error);
    return null;
  }
};

/**
 * 폼 유효성 검증을 위한 헬퍼 함수
 * @param {Object} formSchema - JSON Schema
 * @param {Object} formData - 검증할 데이터
 * @returns {Object} {isValid: boolean, errors: Array}
 */
export const validateFormData = (formSchema, formData) => {
  const errors = [];
  
  if (!formSchema.properties) {
    return { isValid: true, errors: [] };
  }

  // Required 필드 검증
  if (formSchema.required) {
    for (const requiredField of formSchema.required) {
      if (!formData[requiredField] || formData[requiredField] === '') {
        errors.push(`${requiredField}은(는) 필수 입력 항목입니다.`);
      }
    }
  }

  // 개별 필드 유효성 검증
  for (const [fieldName, fieldSchema] of Object.entries(formSchema.properties)) {
    const value = formData[fieldName];
    
    if (value !== undefined && value !== '') {
      // 타입 검증
      if (fieldSchema.type === 'number' && isNaN(Number(value))) {
        errors.push(`${fieldName}은(는) 숫자여야 합니다.`);
      }
      
      // 패턴 검증
      if (fieldSchema.pattern && typeof value === 'string') {
        const regex = new RegExp(fieldSchema.pattern);
        if (!regex.test(value)) {
          errors.push(`${fieldName}의 형식이 올바르지 않습니다.`);
        }
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors: errors
  };
};

/**
 * data_source 쿼리를 실행하여 동적 select 옵션을 가져옵니다.
 * @param {Object} dataSource - data_source 설정 객체
 * @returns {Promise<Array>} 옵션 배열 [{value, label}, ...]
 */
export const fetchSelectOptions = async (dataSource) => {
  if (!dataSource || dataSource.type !== 'sql' || !dataSource.query) {
    return [];
  }

  try {
    const response = await fetch(`${API_BASE_URL}/form_data_source/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        query: dataSource.query
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.options || [];
  } catch (error) {
    console.error('Error fetching select options:', error);
    return [];
  }
}; 